/**
 * Route copy for `src/routes/aiAssistant` (keys: `routes.aiAssistant.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  aiAssistantPage: {
    images: 'Images',
    video: 'Video',
    event: 'Event',
    activityIndicators: 'activity indicators',
    recentRuns: 'recent runs',
    credits: 'credits',
    registryAgents: 'registry agents',
    registryModels: 'registry models',
    aiAssistant: 'AI assistant',
    yourAiCommandCenterLaunchThe: 'Your AI command center: launch the copilot, generate, orchestrate and track.',
    aiActivity: 'AI activity',
    writeWithAi: '✍️ Write with AI',
    someDataCouldNotBeLoaded: 'Some data could not be loaded (',
    theValuesShownForTheseSections: ') — the values shown for these sections are incomplete.',
    tryAgain: 'Try again',
    aiRuns: 'AI runs',
    active: 'Active',
    tokens: 'Tokens',
    cumulativeCost: 'Cumulative cost',
    aiCapabilities: 'AI capabilities',
    open: 'Open',
    recentAiRuns: 'Recent AI runs',
    viewAll: 'View all',
    noAiRunYetLaunchThe: 'No AI run yet. Launch the copilot or generate content to get started.',
    vTok: '{{v}} tok',
    aiRegistry: 'AI registry',
    activeAgents: 'Active agents',
    personas: 'Personas',
    configuredModels: 'Configured models',
    manageTheRegistry: 'Manage the registry →',
    aiCredits: 'AI credits',
    unlimited: 'Unlimited',
    unlimitedCreditsSuperadmin: 'unlimited credits (superadmin)',
    creditsAvailable: 'credits available',
    totalBalance: 'Total balance',
    reserved: 'Reserved',
    lowBalanceConsiderToppingUp: 'Low balance — consider topping up.',
    manageCredits: 'Manage credits →',
    creditWalletUnavailable: 'Credit wallet unavailable.',
    loading: 'Loading…',
    generativeCapabilities: 'Generative capabilities',
    available: 'Available',
    upcoming: 'Upcoming',
  },
  aiData: {
    aiWriting: 'AI writing',
    generatePostsCaptionsAndMultiPlatform: 'Generate posts, captions and multi-platform variants from a topic (ghostwriter).',
    popular: 'Popular',
    imageGeneration: 'Image generation',
    createVisualsWithAiAndSave: 'Create visuals with AI and save them straight to the media library.',
    designStudio: 'Design studio',
    composeCustomVisualsWithTemplatesBrand: 'Compose custom visuals with templates, brand kits and layers.',
    agentsPersonas: 'Agents & personas',
    configureTheRegistrySAiAgents: 'Configure the registry\'s AI agents, personas, prompts, models and skills.',
    aiWorkflows: 'AI workflows',
    orchestrateEndToEndChainsOf: 'Orchestrate end-to-end chains of agents and automated actions.',
    aiActivity: 'AI activity',
    trackAgentRunsInRealTime: 'Track agent runs in real time: steps, tokens, costs, kill switch.',
    aiApprovals: 'AI approvals',
    approveOrRejectPendingAiActions: 'Approve or reject pending AI actions before they run.',
    monitoringIntelligence: 'Monitoring & intelligence',
    hashtagsInfluencersAndCompetitorsTracked: 'Hashtags, influencers and competitors tracked by the analytics engine.',
    aiCredits: 'AI credits',
    monitorAiCreditUsageBalanceAnd: 'Monitor AI credit usage, balance and top-ups.',
    suggest5PostIdeasForThis: 'Suggest 5 post ideas for this week',
    writeAnEngagingCaptionForA: 'Write an engaging caption for a product photo',
    analyzeMyLatestResultsAndSuggest: 'Analyze my latest results and suggest improvements',
    createA7DayContentPlan: 'Create a 7-day content plan for LinkedIn',
    justNow: 'just now',
    mMinAgo: '{{m}} min ago',
    hHAgo: '{{h}} h ago',
    vDAgo: '{{v}} d ago',
    queued: 'queued',
    inProgress: 'in progress',
    awaitingApproval: 'awaiting approval',
    paused: 'paused',
    completed: 'completed',
    failed: 'failed',
    cancelled: 'cancelled',
  },
  copilotActSurface: {
    prepareA2WeekLaunchPlan: 'Prepare a 2-week launch plan for our new offer',
    analyzeMyLast30PostsAnd: 'Analyze my last 30 posts and suggest 5 improvements',
    setUpAFollowUpSequence: 'Set up a follow-up sequence for my inactive leads',
    create3AdVariantsAndSchedule: 'Create 3 ad variants and schedule an A/B test',
    approvalRequired: 'Approval required',
    noApproval: 'No approval',
    thePlanProposalFailedTryAgain: 'The plan proposal failed. Try again.',
    handATaskToTheAi: 'Hand a task to the AI team',
    defaultSuggestion: 'Default suggestion',
    describeYourIntentTheAiTeam: 'Describe your intent: the AI team proposes a plan of concrete actions. Nothing runs automatically — every sensitive step must be approved.',
    eGPrepareAndScheduleA: 'E.g. Prepare and schedule a launch campaign on LinkedIn and Instagram…',
    describeTheTaskForTheAi: 'Describe the task for the AI team',
    buildingThePlan: 'Building the plan…',
    proposeAPlan: '✨ Propose a plan',
    continueInTheCopilot: 'Continue in the Copilot →',
    conversationalPlanProposalIsNotEnabled: '🛠️ Conversational plan proposal is not enabled on this environment yet. Meanwhile, the Copilot answers in natural language.',
    proposedPlanLengthStepS: 'Proposed plan ({{length}} step(s)',
    noStepProposedRephraseYourRequest: 'No step proposed — rephrase your request with more details.',
    contextTakenIntoAccount: 'Context taken into account',
    whatTheAiTeamCanDo: 'What the AI team can do',
  },
  copilotCapabilities: {
    read: 'Read',
    action: 'Action',
    outboundCasl: 'Outbound · CASL',
    whatTheCopilotCanDo: 'What the Copilot can do',
    countTools: '{{count}} tools',
    toolCatalogUnavailableOnThisEnvironment: 'Tool catalog unavailable on this environment.',
    questionsAboutYourData: 'questions about your data',
    executableActions: 'executable actions',
    theCopilotReadsYourDataAutomatically: 'The Copilot reads your data automatically and proposes write actions for confirmation. Outbound actions go through the compliance check (CASL).',
  },
  copilotLauncher: {
    yes: 'yes',
    no: 'no',
    noValueEntered: '(no value entered)',
    submitTheFormidForm: 'Submit the “{{formId}}” form',
    runTheToolAction: 'Run the “{{tool}}” action',
    submitTheForm: 'Submit the form',
    headToolnoteWithTheFollowingValues: '{{head}}{{toolNote}} with the following values:\n{{rendered}}',
    copilot: 'Copilot',
    yourAssistantThatActsInThe: 'Your assistant that acts in the app — writing, navigation, guided actions.',
    copilotMode: 'Copilot mode',
    assistant: 'Assistant',
    agent: 'Agent',
    describeWhatYouWantToAchieve: 'Describe what you want to achieve…',
    messageForTheCopilot: 'Message for the copilot',
    launchTheCopilot: 'Launch the Copilot',
    copilotServiceNotDeployedOnThis: '⚠️ Copilot service not deployed on this environment — your exchanges stay local.',
    latestCopilotReply: 'Latest Copilot reply',
    viewTheConversation: 'View the conversation →',
  },
  copilotSkillsPanel: {
    reservedForAdministratorsOwners: 'Reserved for administrators / owners.',
    errorStatus: 'Error {{status}}',
    unknownError: 'Unknown error',
    builtIn: 'Built-in',
    online: 'Online',
    custom: 'Custom',
    skillNameAdded: 'Skill “{{name}}” added.',
    deleteTheSkill: 'Delete the skill?',
    doYouReallyWantToDelete: 'Do you really want to delete “',
    theCopilotWillNoLongerBe: '”? The Copilot will no longer be able to use it. This cannot be undone.',
    delete: 'Delete',
    skillNameDeleted: 'Skill “{{name}}” deleted.',
    copilotSkills: 'Copilot skills',
    addASkillToTheCopilot: 'Add a skill to the Copilot',
    reservedForAdministratorsOwners2: 'Reserved for administrators / owners',
    add: '+ Add',
    skillManagementUnavailableOnThisEnvironm: 'Skill management unavailable on this environment.',
    aSkillPerformsASpecificTask: 'A skill performs a specific task (analysis, scoring, generation) that the Copilot can call. Disable it to remove it temporarily without deleting it.',
    noSkillConfigured: 'No skill configured.',
    addOneToExtendTheCopilot: ' Add one to extend the Copilot.',
    inactive: 'Inactive',
    saving: 'saving…',
    disable: 'Disable',
    enable: 'Enable',
    deleteName: 'Delete {{name}}',
    newCopilotSkill: 'New Copilot skill',
    theNameIsRequired: 'The name is required.',
    thePromptIdentifierIsRequired: 'The prompt identifier is required.',
    theImplementationReferenceIsRequired: 'The implementation reference is required.',
    name: 'Name *',
    eGContentGapAnalysis: 'e.g. Content gap analysis',
    description: 'Description',
    whatThisSkillDoesInOne: 'What this skill does, in one sentence',
    implementationReference: 'Implementation reference *',
    customPromptPrompt: 'Custom prompt (prompt:…)',
    customReferenceAdvanced: 'Custom reference (advanced)',
    thePythonModuleOrPromptThe: 'The Python module (or prompt) the Copilot will run for this skill.',
    promptIdentifier: 'Prompt identifier *',
    eGProspectFollowupV2: 'e.g. prospect_followup_v2',
    savedAsPrompt: 'Saved as “prompt:',
    fullReference: 'Full reference *',
    modulePathFunction: 'module.path:function',
    add2: 'Add',
    cancel: 'Cancel',
  },
};

export default en;
